import { useFormDataQuery } from '@/api/getters';
import {
  useFormMutation,
  useSubmitStagesMutation,
  useSubmitStepMutation,
} from '@/api/setters';
import { Stages, Steps } from '@/api/types';
import BlockOverlay from '@/components/layouts/BlockOverlay';
import { formMutationDto } from '@/dto/formMutationDto';
import { useStepper } from '@/features/v2/lib/stepper';
import { scrollToTop } from '@/helpers/scrollToTop';

import { useLingui } from '@lingui/react';
import { createPortal } from 'react-dom';

const ConsultantCompletedStageI = () => {
  const { isFetching } = useFormDataQuery();

  const { stepTag, setStepTag, setStep, setInnerStep, innerObject } =
    useStepper();

  const submitStages = useSubmitStagesMutation();

  const submitStep = useSubmitStepMutation();
  const updateForm = useFormMutation();

  const { data } = useFormDataQuery();

  const handleFormCompletion = async () => {
    // in case if consultant completed form before user (edge case)
    if (data?.consultant_completed_at?.stage_I) {
      await updateForm
        .mutateAsync({
          ...formMutationDto({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            form_data: data?.payload?.data as Record<string, any>,
            stepper: {
              step: 1,
              innerStep: {
                [0]: innerObject[0] ?? 0,
                [1]: 0,
              },
            },
          }),
        })
        .then(() => {
          setStep(1);
          setInnerStep({ ...innerObject, [1]: 0 });
          scrollToTop();
        });
    }

    if (!data?.consultant_completed_at?.stage_I) {
      await submitStages.mutateAsync(Stages.STAGE_I_FINISHED);
      scrollToTop();
    }

    if (stepTag === Steps.DOCUMENTS) {
      submitStep.mutateAsync(Steps.ADDITIONAL_INFORMATION).then(() => {
        setStepTag(Steps.ADDITIONAL_INFORMATION);
      });
    }
  };

  return (
    <BlockOverlayPortal
      loading={submitStages.isPending || updateForm.isPending || isFetching}
      handleFormCompletion={handleFormCompletion}
    />
  );
};

export default ConsultantCompletedStageI;

interface BlockOverlayPortalProps {
  handleFormCompletion: () => void;
  loading: boolean;
}

export const BlockOverlayPortal: React.FC<BlockOverlayPortalProps> = ({
  handleFormCompletion,
  loading,
}) => {
  const { i18n } = useLingui();

  return createPortal(
    <BlockOverlay
      loading={loading}
      title={i18n.t('Zajmujemy się Twoją sprawą')}
      info={i18n.t(
        'W tym momencie nie masz możliwości dodawania kolejnych załączników, ponieważ konsultant przegląda sprawę. Przejdź do kolejnych pytań.'
      )}
      buttonConfirm={{
        content: i18n.t('Kontynuuj'),
        onClick: handleFormCompletion,
        withRightArrow: true,
      }}
    />,
    document.body
  );
};
